import { ReferentialResolver } from '@app/core/resolvers/referentials/referential-resolver.service';
import { GroupListComponent } from './groups/group-list/group-list.component';
import { GroupDetailComponent } from './groups/group-detail/group-detail.component';
import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from '@app/core/guards/auth.guard';
import { UserListComponent } from './users/user-list/user-list.component';
import { AccessRequirement } from '@app/core/models/access-requirement.model';
import { UsersResolver } from '@app/core/resolvers/users/users-resolver.service';
import { AssetListComponent } from './assets/assets-list/asset-list.component';
import { AssetsResolver } from '@app/core/resolvers/assets/assets-resolver.service';
import { UserDetailResolver } from '@app/core/resolvers/users/user-detail-resolver.service';
import { UserDetailComponent } from './users/user-detail/user-detail.component';
import { GroupsResolver } from '@app/core/resolvers/groups/groups-resolver.service';
import { GroupResolver } from '@app/core/resolvers/groups/group-resolver.service';
import { RecipientEntityListComponent } from './recipient-entities/recipient-entity-list/recipient-entity-list.component';
import { RecipientEntityDetailComponent } from './recipient-entities/recipient-entity-detail/recipient-entity-detail.component';
import { RecipientEntitiesResolver } from '@app/core/resolvers/recipient-entities/recipient-entities-resolver.service';
import { RecipientEntityResolver } from '@app/core/resolvers/recipient-entities/recipient-entity-resolver.service';
import { WorkflowListComponent } from './workflow/invoices/workflow-list/workflow-list.component';
import { WorkflowsResolver } from '@app/core/resolvers/workflow/workflows-resolver.service';
import { WorkflowDetailResolver } from '@app/core/resolvers/workflow/workflow-detail-resolver.service';
import { AssetDetailComponent } from './assets/assets-detail/asset-detail.component';
import { AssetResolver } from '@app/core/resolvers/assets/asset-resolver.service';
import { ReferentialType } from '@app/core/models/referentials/referential-type.model';
import { EmailTemplateDetailComponent } from './email-templates/email-template-detail/email-template-detail.component';
import { EmailTemplateResolver } from '@app/core/resolvers/email-templates/email-template-resolver.service';
import { EmailTemplateListComponent } from './email-templates/email-template-list/email-template-list.component';
import { EmailTemplatesResolver } from '@app/core/resolvers/email-templates/email-templates-resolver.service';
import { GroupTypeModel } from '@app/core/models/groups/group-type.model';
import { ImportLogsComponent } from './import-logs/import-logs.component';
import { ImportsResolver } from '@app/core/resolvers/imports/imports-resolver.service';
import { TableColumnsConfigKey } from '@app/core/models/configuration/table-columns-config-key.model';
import { ImportDetailsResolver } from '@app/core/resolvers/imports/import-details-resolver.service';
import { ImportLogsDetailComponent } from './import-logs/import-logs-details/import-logs-detail.component';
import { ImportResolver } from '@app/core/resolvers/imports/import-resolver.service';
import { ActivityLogsComponent } from './logs/logs-list/logs.component';
import { ActivitiesResolver } from '@app/core/resolvers/activities-resolver.service';
import { ExcelExportFileName } from '@app/core/models/configuration/excel-export-filename.model';
import { FaultedInvoicesResolver } from '@app/core/resolvers/invoices/faulted-invoices.resolver.service';
import { FaultedInvoicesComponent } from './faulted-invoices/faulted-invoices.component';
import { SessionStorageKeys } from '@app/core/models/configuration/sessionstorage-keys.model';
import { WorkflowDetailComponent } from './workflow/invoices/workflow-detail/workflow-detail.component';
import { WorkflowPromotionListComponent } from './workflow/invoicesPromotion/workflow-promotion-list/workflow-promotion-list.component';
import { WorkflowsPromotionResolver } from '@app/core/resolvers/workflow/workflows-promotion-resolver.service';
import { WorkflowPromotionDetailComponent } from './workflow/invoicesPromotion/workflow-promotion-detail/workflow-promotion-detail.component';
import { WorkflowPromotionDetailResolver } from '@app/core/resolvers/workflow/workflow-promotion-detail-resolver.service';

const adminRootPath = 'administration/';

const routes: Routes = [
  {
    path: adminRootPath + 'users/detail/:id',
    component: UserDetailComponent,
    resolve: {
      userDetail: UserDetailResolver,
    },
    data: { requiredMembership: [AccessRequirement.AdministrationMembership] },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'users/new',
    component: UserDetailComponent,
    data: { requiredMembership: [AccessRequirement.AdministrationMembership] },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'users',
    component: UserListComponent,
    resolve: {
      dataItems: UsersResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      configKey: TableColumnsConfigKey.USER,
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'groups/detail/:id',
    component: GroupDetailComponent,
    resolve: {
      group: GroupResolver,
      referentials: ReferentialResolver,
    },
    data: {
      requiredMembership:
      [
        AccessRequirement.OperationalMembership,
        AccessRequirement.ValidationMembership,
        AccessRequirement.SupervisionMembership,
        AccessRequirement.AdministrationMembership,
        AccessRequirement.NonErpMemberShip,
        AccessRequirement.MultiMembership,
        AccessRequirement.InvoiceRelatedGroupMembership,
        AccessRequirement.ControlMembership,
        AccessRequirement.ControlNotPPRMembership,
        AccessRequirement.ObservationMembership,
        AccessRequirement.AnalyticalMembership,
        AccessRequirement.OperationContributorMembership,
        AccessRequirement.PPRValidationMembership,
        AccessRequirement.PPRCancelationMembership,
      ],
      referentialsTypes: [
        ReferentialType.GROUPTYPES,
        ReferentialType.PROFESSIONTYPES,
        ReferentialType.TYPOLOGIE,
        ReferentialType.COMPANY,
        ReferentialType.TREELEVEL,
      ],
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'groups/new',
    component: GroupDetailComponent,
    resolve: {
      referentials: ReferentialResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      referentialsTypes: [
        ReferentialType.GROUPTYPES,
        ReferentialType.PROFESSIONTYPES,
        ReferentialType.TYPOLOGIE,
        ReferentialType.COMPANY,
        ReferentialType.TREELEVEL,
      ],
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'groups',
    component: GroupListComponent,
    resolve: {
      referentials: ReferentialResolver,
    },
    data: {
      requiredMembership:
      [
        AccessRequirement.OperationalMembership,
        AccessRequirement.ValidationMembership,
        AccessRequirement.SupervisionMembership,
        AccessRequirement.AdministrationMembership,
        AccessRequirement.NonErpMemberShip,
        AccessRequirement.MultiMembership,
        AccessRequirement.InvoiceRelatedGroupMembership,
        AccessRequirement.ControlMembership,
        AccessRequirement.ControlNotPPRMembership,
        AccessRequirement.ObservationMembership,
        AccessRequirement.AnalyticalMembership,
        AccessRequirement.OperationContributorMembership,
        AccessRequirement.PPRValidationMembership,
        AccessRequirement.PPRCancelationMembership,
      ],
      referentialsTypes: [ReferentialType.GROUPTYPES],
      configKey: TableColumnsConfigKey.GROUP,
      searchFilterKey: SessionStorageKeys.ADMINISTRATION_GROUPS,
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'assets',
    component: AssetListComponent,
    resolve: {
      dataItems: AssetsResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      configKey: TableColumnsConfigKey.ASSET,
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'assets/new',
    component: AssetDetailComponent,
    resolve: {
      referentials: ReferentialResolver,
      recipientEntities: RecipientEntitiesResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      referentialsTypes: [ReferentialType.ASSETTYPES],
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'assets/detail/:id',
    component: AssetDetailComponent,
    resolve: {
      asset: AssetResolver,
      referentials: ReferentialResolver,
      recipientEntities: RecipientEntitiesResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      referentialsTypes: [ReferentialType.ASSETTYPES],
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'workflows',
    component: WorkflowListComponent,
    resolve: {
      dataItems: WorkflowsResolver,
    },
    data: {
      requiredMembership:
      [
        AccessRequirement.OperationalMembership,
        AccessRequirement.ValidationMembership,
        AccessRequirement.SupervisionMembership,
        AccessRequirement.AdministrationMembership,
        AccessRequirement.NonErpMemberShip,
        AccessRequirement.MultiMembership,
        AccessRequirement.InvoiceRelatedGroupMembership,
        AccessRequirement.ControlMembership,
        AccessRequirement.ControlNotPPRMembership,
        AccessRequirement.ObservationMembership,
        AccessRequirement.AnalyticalMembership,
        AccessRequirement.OperationContributorMembership,
        AccessRequirement.PPRValidationMembership,
        AccessRequirement.PPRCancelationMembership,
      ],
      configKey: TableColumnsConfigKey.WORKFLOW,
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'workflows/detail/:id',
    component: WorkflowDetailComponent,
    resolve: {
      workflowDetail: WorkflowDetailResolver,
      groups: GroupsResolver,
    },
    data: {
      requiredMembership:
      [
        AccessRequirement.OperationalMembership,
        AccessRequirement.ValidationMembership,
        AccessRequirement.SupervisionMembership,
        AccessRequirement.AdministrationMembership,
        AccessRequirement.NonErpMemberShip,
        AccessRequirement.MultiMembership,
        AccessRequirement.InvoiceRelatedGroupMembership,
        AccessRequirement.ControlMembership,
        AccessRequirement.ControlNotPPRMembership,
        AccessRequirement.ObservationMembership,
        AccessRequirement.AnalyticalMembership,
        AccessRequirement.OperationContributorMembership,
        AccessRequirement.PPRValidationMembership,
        AccessRequirement.PPRCancelationMembership,
      ],
      groupTypes: [
        GroupTypeModel.NONERPCOMPTABILITY,
        GroupTypeModel.OPERATIONAL,
        GroupTypeModel.VALIDATION,
      ],
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'workflows/new',
    component: WorkflowDetailComponent,
    resolve: {
      groups: GroupsResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      groupTypes: [
        GroupTypeModel.NONERPCOMPTABILITY,
        GroupTypeModel.OPERATIONAL,
        GroupTypeModel.VALIDATION,
      ],
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'workflowsPromotion',
    component: WorkflowPromotionListComponent,
    resolve: {
      dataItems: WorkflowsPromotionResolver,
    },
    data: {
      requiredMembership:
      [
        AccessRequirement.OperationalMembership,
        AccessRequirement.ValidationMembership,
        AccessRequirement.SupervisionMembership,
        AccessRequirement.AdministrationMembership,
        AccessRequirement.NonErpMemberShip,
        AccessRequirement.MultiMembership,
        AccessRequirement.InvoiceRelatedGroupMembership,
        AccessRequirement.ControlMembership,
        AccessRequirement.ControlNotPPRMembership,
        AccessRequirement.ObservationMembership,
        AccessRequirement.AnalyticalMembership,
        AccessRequirement.OperationContributorMembership,
        AccessRequirement.PPRValidationMembership,
        AccessRequirement.PPRCancelationMembership,
      ],
      configKey: TableColumnsConfigKey.WORKFLOW_PROMOTION,
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'workflowsPromotion/detail/:id',
    component: WorkflowPromotionDetailComponent,
    resolve: {
      workflowDetail: WorkflowPromotionDetailResolver,
      groups: GroupsResolver,
    },
    data: {
      requiredMembership:
      [
        AccessRequirement.OperationalMembership,
        AccessRequirement.ValidationMembership,
        AccessRequirement.SupervisionMembership,
        AccessRequirement.AdministrationMembership,
        AccessRequirement.NonErpMemberShip,
        AccessRequirement.MultiMembership,
        AccessRequirement.InvoiceRelatedGroupMembership,
        AccessRequirement.ControlMembership,
        AccessRequirement.ControlNotPPRMembership,
        AccessRequirement.ObservationMembership,
        AccessRequirement.AnalyticalMembership,
        AccessRequirement.OperationContributorMembership,
        AccessRequirement.PPRValidationMembership,
        AccessRequirement.PPRCancelationMembership,
      ],
      groupTypes: [
        GroupTypeModel.OPERATIONAL,
        GroupTypeModel.OPERATIONCONTRIBUTOR,
        GroupTypeModel.PPR_VALIDATION,
      ], },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'workflowsPromotion/new',
    component: WorkflowPromotionDetailComponent,
    resolve: {
      groups: GroupsResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      groupTypes: [
        GroupTypeModel.OPERATIONAL,
        GroupTypeModel.OPERATIONCONTRIBUTOR,
        GroupTypeModel.PPR_VALIDATION,
      ],
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'recipient-entities',
    component: RecipientEntityListComponent,
    resolve: {
      dataItems: RecipientEntitiesResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      configKey: TableColumnsConfigKey.RECIPIENTENTITY,
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'recipient-entities/detail/:id',
    component: RecipientEntityDetailComponent,
    resolve: {
      recipientEntity: RecipientEntityResolver,
    },
    data: { requiredMembership: [AccessRequirement.AdministrationMembership] },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'recipient-entities/new',
    component: RecipientEntityDetailComponent,
    data: { requiredMembership: [AccessRequirement.AdministrationMembership] },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'email-templates/detail/:id',
    component: EmailTemplateDetailComponent,
    resolve: {
      emailTemplate: EmailTemplateResolver,
      referentials: ReferentialResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      referentialsTypes: [ReferentialType.EMAILTEMPLATETYPES],
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'email-templates/new',
    component: EmailTemplateDetailComponent,
    resolve: {
      referentials: ReferentialResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      referentialsTypes: [ReferentialType.EMAILTEMPLATETYPES],
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'email-templates',
    component: EmailTemplateListComponent,
    resolve: {
      dataItems: EmailTemplatesResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      configKey: TableColumnsConfigKey.EMAILTEMPLATE,
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'import-logs',
    component: ImportLogsComponent,
    resolve: {
      imports: ImportsResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      configKey: TableColumnsConfigKey.IMPORTHISTORY,
      exportFileName: ExcelExportFileName.IMPORTHISTORY,
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'import-logs/:id',
    component: ImportLogsDetailComponent,
    resolve: {
      import: ImportResolver,
      details: ImportDetailsResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      configKey: TableColumnsConfigKey.IMPORTHISTORYDETAIL,
      exportFileName: ExcelExportFileName.IMPORTHISTORYDETAIL,
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'logs',
    component: ActivityLogsComponent,
    resolve: {
      dataItems: ActivitiesResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      configKey: TableColumnsConfigKey.ACTIVITYLOGS,
      exportFileName: ExcelExportFileName.ACTIVITIYLOGS,
    },
    canActivate: [AuthGuard],
  },
  {
    path: adminRootPath + 'faulted-invoice',
    component: FaultedInvoicesComponent,
    resolve: {
      invoices: FaultedInvoicesResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AdministrationMembership],
      configKey: TableColumnsConfigKey.FAULTEDINVOICES,
      exportFileName: ExcelExportFileName.FAULTEDINVOICES,
    },
    canActivate: [AuthGuard],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class AdministrationRoutingModule {}
